import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import { Box, Button, CircularProgress, Collapse, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import type { BiAgentMessage as BiAgentMessageType } from "../../../../../../api/types/biAgentTypes";
import type { ReportConfiguration, ReportType } from "../../../../../../api/types/svTypes";
import UndoIcon from "@entrilia/shared/icons/UndoIcon";
import { BiAgentInput } from "./BiAgentInput";
import { BiAgentMessage } from "./BiAgentMessage";

const formatExecutionTime = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

interface BiAgentChatContainerProps {
  messages: BiAgentMessageType[];
  isLoading: boolean;
  statusMessage?: string;
  appliedMessages: Map<string, { configuration: ReportConfiguration; reportType?: ReportType }>;
  onSendMessage: (message: string) => void;
  onSelectOption: (option: string) => void;
  onApplyConfiguration?: (
    messageId: string,
    configuration: ReportConfiguration,
    reportType?: ReportType,
    dataSetId?: string
  ) => void;
  onRevertConfiguration?: (messageId: string) => void;
}

export const BiAgentChatContainer = ({
  messages,
  isLoading,
  statusMessage,
  appliedMessages,
  onSendMessage,
  onSelectOption,
  onApplyConfiguration,
  onRevertConfiguration,
}: BiAgentChatContainerProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [collapsedClarificationMessageIds, setCollapsedClarificationMessageIds] = useState<string[]>([]);
  const [selectedClarificationMessageIds, setSelectedClarificationMessageIds] = useState<string[]>([]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const latestMessage = messages[messages.length - 1];
  const latestMessageId = latestMessage?.id;

  const handleToggleClarificationOptions = (messageId: string) => {
    setCollapsedClarificationMessageIds((currentMessageIds) =>
      currentMessageIds.includes(messageId)
        ? currentMessageIds.filter((currentMessageId) => currentMessageId !== messageId)
        : [...currentMessageIds, messageId]
    );
  };

  const handleSelectOption = (messageId: string, option: string) => {
    setSelectedClarificationMessageIds((currentMessageIds) =>
      currentMessageIds.includes(messageId) ? currentMessageIds : [...currentMessageIds, messageId]
    );
    setCollapsedClarificationMessageIds((currentMessageIds) =>
      currentMessageIds.includes(messageId) ? currentMessageIds : [...currentMessageIds, messageId]
    );
    onSelectOption(option);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Stack spacing={1.5} sx={{ flex: 1, overflow: "auto", p: 2 }}>
        {messages.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 2,
              px: 3,
              textAlign: "center",
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 36, color: "primary.main" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Hi! I&apos;m your SmartViews Assistant.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 340 }}>
              I can build and update report configurations from plain language. Just describe what you need:
            </Typography>
            <Box
              component="ul"
              sx={{ m: 0, pl: 2.5, textAlign: "left", "& li": { mt: 0.75 }, "& li::marker": { color: "primary.main" } }}
            >
              {[
                "Create a trial balance for entity AQ I as of today",
                "Create a new Investor Rollforward for entity AQ I as of the end of last year",
                "Add a fair value measure with ending balance",
                "Filter by investor and group by portfolio company",
              ].map((example) => (
                <Box component="li" key={example}>
                  <Typography variant="body2" color="textPrimary">
                    {example}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <>
            {messages.map((message) => (
              <Box key={message.id}>
                <BiAgentMessage message={message} />
                {message.configuration !== undefined && onApplyConfiguration && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {appliedMessages.has(message.id) ? (
                        <>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "success.main" }}>
                            <CheckRoundedIcon fontSize="small" />
                            <Typography variant="body2">Applied</Typography>
                          </Box>
                          {onRevertConfiguration && (
                            <Tooltip title="Revert" arrow disableInteractive>
                              <IconButton
                                size="small"
                                aria-label="Revert"
                                onClick={() => onRevertConfiguration(message.id)}
                              >
                                <UndoIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            if (message.configuration) {
                              onApplyConfiguration(
                                message.id,
                                message.configuration,
                                message.reportType,
                                message.dataSetId
                              );
                            }
                          }}
                          sx={{ textTransform: "none" }}
                        >
                          Apply
                        </Button>
                      )}
                    </Box>
                    {message.executionTimeMs !== undefined && (
                      <Typography variant="caption" color="textSecondary">
                        {formatExecutionTime(message.executionTimeMs)}
                      </Typography>
                    )}
                  </Box>
                )}
                {message.clarification && message.clarification.options.length > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", px: 1 }}>
                    <Stack sx={{ alignItems: "flex-start", gap: 0.5 }}>
                      <Button
                        variant="text"
                        color="inherit"
                        endIcon={
                          collapsedClarificationMessageIds.includes(message.id) ? (
                            <KeyboardArrowDownRoundedIcon />
                          ) : (
                            <KeyboardArrowUpRoundedIcon />
                          )
                        }
                        onClick={() => handleToggleClarificationOptions(message.id)}
                        sx={{ color: "text.secondary", textTransform: "none" }}
                      >
                        {collapsedClarificationMessageIds.includes(message.id) ? "Show all variants" : "Hide variants"}
                      </Button>
                      <Collapse in={!collapsedClarificationMessageIds.includes(message.id)} unmountOnExit>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {message.clarification.options.map((option, index) => (
                            <Button
                              // eslint-disable-next-line @eslint-react/no-array-index-key
                              key={index}
                              variant="outlined"
                              color="secondary"
                              size="small"
                              onClick={() => handleSelectOption(message.id, option)}
                              disabled={
                                isLoading ||
                                selectedClarificationMessageIds.includes(message.id) ||
                                message.id !== latestMessageId
                              }
                              sx={{ textTransform: "none" }}
                            >
                              {option}
                            </Button>
                          ))}
                        </Box>
                      </Collapse>
                    </Stack>
                    {message.executionTimeMs !== undefined && (
                      <Typography variant="caption" color="textSecondary" sx={{ flexShrink: 0, ml: 1, mt: 1 }}>
                        {formatExecutionTime(message.executionTimeMs)}
                      </Typography>
                    )}
                  </Box>
                )}
                {!message.configuration &&
                  (!message.clarification || message.clarification.options.length === 0) &&
                  message.executionTimeMs !== undefined &&
                  message.role === "assistant" && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1, mr: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        {formatExecutionTime(message.executionTimeMs)}
                      </Typography>
                    </Box>
                  )}
              </Box>
            ))}
            {isLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="textSecondary">
                  {statusMessage ?? "Thinking..."}
                </Typography>
              </Box>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </Stack>
      <BiAgentInput onSend={onSendMessage} disabled={isLoading} />
    </Box>
  );
};
